// src/prototype/enterprise/useEnterpriseMode.ts
// ============================================================
// ENTERPRISE MODE — the Standard ⟷ Enterprise flip (X-1)
//
// Same client (ACME), different entitlement — NOT a role/persona switch. Reuses the
// dva_demo_plan_override PATTERN (a localStorage override synced across hook instances via
// a custom event), sibling to useDemoPlan. Sticky STATE: the dropdown pill is the only
// thing that enters/exits it, so journey-step navigation keeps the navy bar present.
//
// ENTITLEMENT RULE: Enterprise is inherently a Pro-tier capability — there is no
// freemium-enterprise state. Entering Enterprise FORCES the shared tier override to Pro
// (remembering the prior tier); exiting RESTORES the prior tier. This drives the SAME
// dva_demo_plan_override the storefront uses (via useDemoPlan) — no parallel entitlement
// path. Persisted so a reload mid-Enterprise stays Pro and still restores on exit.
//
// Only active when the enterprise prototype flag is on; otherwise always 'standard'.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { isEnterprisePrototypeEnabled } from '../data/adapters/enterprisePrototypeFlag';
import { DEMO_PLAN_KEY, DEMO_PLAN_CHANGE_EVENT } from '../../hooks/useDemoPlan';

export type EnterpriseMode = 'standard' | 'enterprise';

export const ENTERPRISE_MODE_KEY = 'dva_demo_enterprise_mode';
const CHANGE_EVENT = 'dva_demo_enterprise_mode_change';
/** Where the pre-Enterprise tier is stashed so exit can restore it (survives reload). */
const PRIOR_PLAN_KEY = 'dva_demo_prior_plan';
const NONE = '__none__'; // sentinel: "no override was set → real plan"

type PlanOverride = 'freemium' | 'pro';

function readMode(): EnterpriseMode {
  if (typeof window === 'undefined') return 'standard';
  return localStorage.getItem(ENTERPRISE_MODE_KEY) === 'enterprise' ? 'enterprise' : 'standard';
}

/** Drive the shared tier override + notify every useDemoPlan instance live. */
function applyPlanOverride(value: PlanOverride | null): void {
  if (typeof window === 'undefined') return;
  if (value === null) localStorage.removeItem(DEMO_PLAN_KEY);
  else localStorage.setItem(DEMO_PLAN_KEY, value);
  window.dispatchEvent(new CustomEvent(DEMO_PLAN_CHANGE_EVENT, { detail: value }));
}

/** Enterprise requires Pro: force Pro on enter (stashing the prior tier), restore on exit. */
function coupleEntitlement(next: EnterpriseMode, prevMode: EnterpriseMode): void {
  if (typeof window === 'undefined') return;
  if (next === 'enterprise' && prevMode !== 'enterprise') {
    const prior = localStorage.getItem(DEMO_PLAN_KEY); // 'freemium' | 'pro' | null (real)
    localStorage.setItem(PRIOR_PLAN_KEY, prior ?? NONE);
    applyPlanOverride('pro');
  } else if (next === 'standard' && prevMode === 'enterprise') {
    const stashed = localStorage.getItem(PRIOR_PLAN_KEY);
    applyPlanOverride(stashed && stashed !== NONE ? (stashed as PlanOverride) : null);
    localStorage.removeItem(PRIOR_PLAN_KEY);
  }
}

export interface EnterpriseModeApi {
  /** Whether the enterprise prototype is enabled for this build. */
  enabled: boolean;
  mode: EnterpriseMode;
  isEnterprise: boolean;
  setMode: (mode: EnterpriseMode) => void;
  toggle: () => void;
}

export function useEnterpriseMode(): EnterpriseModeApi {
  const enabled = isEnterprisePrototypeEnabled();
  const [mode, setModeState] = useState<EnterpriseMode>(() => (enabled ? readMode() : 'standard'));

  // Keep every hook instance in sync when the flip happens anywhere.
  useEffect(() => {
    const handle = (e: Event) => setModeState((e as CustomEvent<EnterpriseMode>).detail ?? readMode());
    window.addEventListener(CHANGE_EVENT, handle as EventListener);
    return () => window.removeEventListener(CHANGE_EVENT, handle as EventListener);
  }, []);

  const setMode = useCallback((next: EnterpriseMode) => {
    const prev = readMode();
    // Force/restore the Pro entitlement on the enter/exit transition BEFORE flipping mode.
    coupleEntitlement(next, prev);
    if (typeof window !== 'undefined') localStorage.setItem(ENTERPRISE_MODE_KEY, next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
    setModeState(next);
  }, []);

  const toggle = useCallback(() => setMode(readMode() === 'enterprise' ? 'standard' : 'enterprise'), [setMode]);

  const effective: EnterpriseMode = enabled ? mode : 'standard';
  return { enabled, mode: effective, isEnterprise: enabled && effective === 'enterprise', setMode, toggle };
}

export default useEnterpriseMode;
